import {forkJoin as observableForkJoin, Observable, of as observableOf} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpRepository} from './http.repository';
import {Line} from '../interfaces/rest/Line';
import {GlobalConfig} from '../configs/GlobalConfig';
import {HttpClient} from "@angular/common/http";
import {PagingRequest} from "../interfaces/rest/paging/PagingRequest";
import {PagedData} from "../interfaces/rest/paging/PagedData";
import {Plant} from "../interfaces/rest/Plant";
import {UserSessionRepository} from "./user-session.repository";
import {PlantRepository} from "./plant.repository";
import {UserRepository} from "./user.repository";
import {flatMap, switchMap} from "rxjs/operators";
import {User} from "../interfaces/rest/User";
import {Filter} from "../interfaces/rest/paging/Filter";

@Injectable()
export class LineRepository {

  constructor(protected http: HttpClient,
              protected httpRepository: HttpRepository,
              protected userRepository: UserRepository,
              protected userSessionRepository: UserSessionRepository,
              protected plantRepository: PlantRepository,
  ) {
  }

  /**
   * Get list of lines by plant ID.
   * @param plantId
   * @param userId
   * @returns {Observable<Array<Line>>}
   */
  getLinesByPlant(plantId: string | number | undefined, userId: number | null = null): Observable<Array<Line>> {
    let endpoint = `${GlobalConfig.endpoints.plants}/${plantId}/lines`;
    if (userId) {
      endpoint += `?userId=${userId}`;
    }
    return this.httpRepository.get(endpoint);
  }

  /**
   * Get list of lines by plant IDs.
   * @param plantIds
   * @param userId
   * @returns {Observable<Array<Array<Line>>>}
   */
  getLinesByPlants(plantIds: Array<string | number | undefined>, userId: number | null = null): Observable<Array<Array<Line>>> {
    let observables: Array<Observable<Array<Line>>> = [];
    plantIds.forEach(plantId => {
      observables.push(this.getLinesByPlant(plantId, userId));
    });

    return observableForkJoin(observables);
  }

  /**
   * Get all lines
   * @returns {Observable<Array<Line>>}
   */
  getLines(): Observable<Array<Line>> {
    return this.httpRepository.get(GlobalConfig.endpoints.lines);
  }

  getLinesWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    return this.httpRepository.post(`${GlobalConfig.endpoints.lines}/paging`, pagingRequest);
  }

  getLinesByCurrentUserWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    let currentUser = this.userSessionRepository.getUserSession();
    if (currentUser!.userType === 'see') {
      return this.getLinesWithPaging(pagingRequest);
    } else {
      return this.userRepository.getUser(Number(currentUser!.userId)).pipe(
        flatMap((userData: User) => {
          const newPagingRequest = new PagingRequest();
          newPagingRequest.pageSize = pagingRequest.pageSize;
          newPagingRequest.page = pagingRequest.page;
          newPagingRequest.sorts = pagingRequest.sorts;
          newPagingRequest.filters = pagingRequest!.filters;
          if (userData.lineBarAccess && userData.lineBarAccess.length > 0) {
            const filter = new Filter();
            filter.property = 'lineBarConfig.user.userId';
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.userId!.toString();
            newPagingRequest.filters.push(filter);
          } else if (userData.plantAccess && userData.plantAccess.length > 0) {
            const filter = new Filter();
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.userId!.toString();
            filter.property = 'plant.plantAccess.user.userId';
            newPagingRequest.filters.push(filter);
          } else {
            const filter = new Filter();
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.clientId;
            filter.property = 'plant.ownedBy.companyId';
            newPagingRequest.filters.push(filter);
          }
          return this.getLinesWithPaging(newPagingRequest);
        }));

    }
  }


  /**
   * Get line by line ID.
   * @param lineId
   * @returns {Observable<Line>}
   */
  getLine(lineId: string | number): Observable<Line> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.lines}/${lineId}`);
  }

  /**
   * Get lines by line IDs.
   * @param lineIds
   * @returns {Observable<any>}
   */
  getLinesByLineIds(lineIds: Array<string | number>) {
    let observables: Array<Observable<Line>> = [];
    lineIds.forEach((lineId) => {
      observables.push(this.getLine(lineId));
    });

    return observableForkJoin(observables);
  }

  /**
   * Get Asset List for a specific Line.
   * @param lineId
   * @returns {Observable<any>}
   */
  getShiftsForLine(lineId: string | number) {
    return this.httpRepository.get(`${GlobalConfig.endpoints.lines}/${lineId}/shifts`);
  }

  updateLineByPlantId(lineId: string | number, plantId: string | number, payload: Line) {
    return this.httpRepository.put(`${GlobalConfig.endpoints.plants}/${plantId}/lines/${lineId}`, payload);
  }

  addLineByPlantId(plantId: string | number, payload: Line) {
    return this.httpRepository.post(`${GlobalConfig.endpoints.plants}/${plantId}/lines`, payload);
  }

  deleteLine(plantId: string | number, lineId: string | number) {
    return this.httpRepository.delete(`${GlobalConfig.endpoints.plants}/${plantId}/lines/${lineId}`);
  }


  /**
   * If current user is SEE, then get ALL lines. If current user is external user, then get
   * all lines under the client of the external user.
   * @returns {Observable<Array<Line>>}
   */
  getLinesByCurrentUser(filterByLineAccess = false): Observable<Array<Line>> {
    if (this.userSessionRepository.getUserSession()?.userType === 'see') {
      return this.getLines();
    } else {
      return this.userRepository.getUser(this.userSessionRepository.getUserSession()?.userId).pipe(
        switchMap((userData: User) => {
          let clientId = User.getCompanyId(userData);
          if (filterByLineAccess) {
            return this.getLinesByClient(clientId!, userData);
          }
          return this.getLinesByClient(clientId!, null);
        }));
    }
  }

  /**
   * Method used to get lines by client.
   * @param {number} clientId - The client id.
   * @param {User} userData -Contains user data.
   *
   * @returns {Observable<Array<Line>>}
   */
  getLinesByClient(clientId: number, userData: User | null = null): Observable<Array<Line>> {
    let userId = null;
    if (userData) {
      userId = userData.userId;
    }
    return this.plantRepository.getPlantsByClient(clientId, userId!).pipe(
      switchMap((plantsData: Array<Plant>) => {
        if (plantsData && plantsData.length) {
          let plantIds: Array<number | undefined> = [];
          plantIds = plantsData.map((plantData: Plant) => {
            return plantData.plantId;
          });
          return this.getLinesByPlants(plantIds, userId).pipe(
            switchMap((data: Array<Array<Line>>) => {
              let lines: Array<Line> = [];
              if (data) {
                data.forEach((linesData: Array<Line>) => {
                  if (linesData) {
                    linesData.forEach((lineData: Line) => {
                      lines.push(lineData);
                    });
                  }
                });
              }

              return observableOf(lines);
            }));
        } else {
          return observableOf([]);
        }
      }));
  }


}
